import React, { useMemo, useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import { Input, Checkbox } from '../ui/Field/Field';
import { PERMISSIONS, PERMISSION_GROUPS, PERMISSION_LABELS, MODULE_LABELS, MODULE_ORDER, MODULE_HINTS } from '../constants/permissions';
import { ADMIN_LOCKED, KINDS, KIND_ORDER, PRESETS } from '../constants/permissionCatalog';
import { kindOf, levelOf, presetChoices, presetKeys, LEVEL_WORDS, LEVEL_TONE } from '../constants/permissionLevels';

const PRESET_WORDS = { none: 'None', view: PRESETS.view.label, staff: PRESETS.staff.label, full: PRESETS.full.label };

// One editor for every place permissions are granted (roles and single users) - page by page, in three levels
// (owner's permissions plan, 3 Oct 2026): opening the page, its tabs, its buttons - the buttons by their kind (edit,
// export, run, write on Ejar, delete, admin ...). Each page says its level (No access / View only / Staff / Full /
// Custom) and has its shortcuts. A search box, "Grant all / Clear all", and - when a baseline is given (the role's
// permissions while looking at a user) - a mark on what this user gets extra or loses.
//
// props: value (array of permission keys), onChange(nextArray), baseline (array | null),
//        readOnly (bool), title (node)
const sectionOrder = () => {
  const known = MODULE_ORDER.filter((g) => PERMISSION_GROUPS[g]);
  const rest = Object.keys(PERMISSION_GROUPS).filter((g) => !known.includes(g));
  return [...known, ...rest];
};

// Safety net: a permission that exists but was forgotten in every group still shows up
// (under "Other"), so nothing can be impossible to grant.
const ungrouped = () => {
  const grouped = new Set(Object.values(PERMISSION_GROUPS).flat());
  return Object.keys(PERMISSIONS).filter((p) => !grouped.has(p));
};

export const labelOf = (p) => PERMISSION_LABELS[p] || p.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

export default function PermissionsEditor({ value, onChange, baseline = null, readOnly = false, title = null }) {
  const [q, setQ] = useState('');
  const selected = useMemo(() => new Set(value || []), [value]);
  const base = useMemo(() => (baseline ? new Set(baseline) : null), [baseline]);
  const sections = useMemo(() => {
    const out = sectionOrder().map((g) => ({ id: g, perms: PERMISSION_GROUPS[g] }));
    const extra = ungrouped();
    if (extra.length) out.push({ id: 'other', perms: extra });
    return out;
  }, []);
  // each page's level and shortcuts, worked out once per change (not per page per render)
  const levels = useMemo(() => {
    const list = [...selected];
    return Object.fromEntries(sections.map(({ id, perms }) => [id, id === 'other'
      ? (perms.some((p) => selected.has(p)) ? 'custom' : 'none') : levelOf(list, id)]));
  }, [selected, sections]);
  const choices = useMemo(() => Object.fromEntries(sections.filter(({ id }) => id !== 'other')
    .map(({ id }) => [id, presetChoices(id)])), [sections]);
  const needle = q.trim().toLowerCase();
  const matches = (p) => !needle || p.includes(needle) || labelOf(p).toLowerCase().includes(needle);

  const set = (perms, on) => {
    if (readOnly) return;
    const next = new Set(selected);
    perms.forEach((p) => (on && !ADMIN_LOCKED.has(p) ? next.add(p) : next.delete(p)));
    onChange([...next]);
  };
  const toggle = (p) => set([p], !selected.has(p));
  // a shortcut: the page holds exactly that level's permissions
  const applyPreset = (pageId, perms, preset) => {
    if (readOnly) return;
    const next = new Set(selected);
    perms.forEach((p) => next.delete(p));
    presetKeys(pageId, preset).forEach((p) => next.add(p));
    onChange([...next]);
  };

  const total = Object.keys(PERMISSIONS).length;
  const extraCount = base ? [...selected].filter((p) => !base.has(p)).length : 0;
  const lostCount = base ? [...base].filter((p) => !selected.has(p)).length : 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="ds-toolbar">
        {title}
        <span className="text-sm font-semibold text-gray-700">{selected.size} of {total} permissions</span>
        {base && (extraCount || lostCount) ? (
          <span className="text-sm font-medium">
            {extraCount ? <span className="text-emerald-700">+{extraCount} beyond the role</span> : null}
            {extraCount && lostCount ? ' · ' : ''}
            {lostCount ? <span className="text-red-700">−{lostCount} removed from the role</span> : null}
          </span>
        ) : null}
        <span className="ds-toolbar__spacer" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a permission or a page…"
          aria-label="Search permissions" size="sm" className="w-full sm:w-72" />
        {!readOnly && (
          <>
            <Button variant="ghost" size="sm" onClick={() => set(Object.keys(PERMISSIONS), true)}>Grant all</Button>
            <Button variant="ghost" size="sm" onClick={() => set(Object.keys(PERMISSIONS), false)}>Clear all</Button>
          </>
        )}
      </div>

      {sections.map(({ id, perms }) => {
        const shown = perms.filter(matches);
        if (!shown.length) return null;
        const on = perms.filter((p) => selected.has(p)).length;
        const level = levels[id];
        const pageName = MODULE_LABELS[id] || (id === 'other' ? 'Other' : id.replace(/_/g, ' '));
        const byKind = KIND_ORDER.map((k) => [k, shown.filter((p) => kindOf(p) === k)]).filter(([, ps]) => ps.length);
        return (
          <Card
            key={id}
            id={`perm-page-${id}`}
            padding="none"
            className="overflow-hidden scroll-mt-24"
            header={(
              <div className="flex-1 flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-gray-200 bg-gray-50">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">{pageName}</h4>
                  {MODULE_HINTS[id] && <div className="text-xs text-gray-600">{MODULE_HINTS[id]}</div>}
                </div>
                <Badge size="sm" variant={LEVEL_TONE[level]}>{LEVEL_WORDS[level]}</Badge>
                <span className="text-xs font-medium text-gray-600 tabular-nums">{on} / {perms.length}</span>
                {!readOnly && id !== 'other' && (
                  <span className="ml-auto flex flex-wrap gap-1" role="group" aria-label={`${pageName}: shortcuts`}>
                    {choices[id].map((preset) => (
                      <Button key={preset} variant={level === preset ? 'secondary' : 'ghost'} size="xs"
                        aria-pressed={level === preset} onClick={() => applyPreset(id, perms, preset)}>{PRESET_WORDS[preset]}</Button>
                    ))}
                  </span>
                )}
              </div>
            )}
          >
            <div className="flex flex-col">
              {byKind.map(([kind, ps]) => (
                <div key={kind} className="flex flex-col sm:flex-row sm:items-start gap-2 px-3 py-2 border-t border-gray-100 first:border-t-0">
                  <div className="sm:w-44 shrink-0 pt-1.5">
                    <Badge size="sm" variant={KINDS[kind].tone}>{KINDS[kind].label}</Badge>
                  </div>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1">
                    {ps.map((p) => {
                      const locked = ADMIN_LOCKED.has(p);         // the Admin role's own: never granted to a role
                      const isOn = !locked && selected.has(p);
                      const mark = base ? (isOn && !base.has(p) ? 'extra' : !isOn && base.has(p) ? 'removed' : '') : '';
                      return (
                        <Checkbox
                          key={p}
                          checked={isOn}
                          disabled={readOnly || locked}
                          onChange={() => toggle(p)}
                          className={`px-2 py-1.5 rounded-md ${readOnly ? '' : 'hover:bg-gray-50'} ${mark === 'extra' ? 'bg-emerald-50' : mark === 'removed' ? 'bg-red-50' : ''}`}
                          label={(
                            <>
                              {labelOf(p)}
                              {locked && <Badge size="sm" variant="neutral" className="ml-1">Admin only</Badge>}
                              {mark === 'extra' && <Badge size="sm" variant="success" className="ml-1">extra</Badge>}
                              {mark === 'removed' && <Badge size="sm" variant="danger" className="ml-1">removed</Badge>}
                            </>
                          )}
                          description={<span className="font-mono text-[11px]">{p}</span>}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
